import React, { useState, useEffect, useRef } from 'react';
import { Modal, Typography, Spin, message, Form, Button, Input, Select } from 'antd';
import { Document, Page, pdfjs } from 'react-pdf';
import api from '../utils/api';
import ReactMarkdown from 'react-markdown';
import './PreviewAndResult.css';
import { useSearchParams } from 'react-router-dom';

const { Option } = Select;
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;
const { Title, Text } = Typography;

interface AuditIssue {
  issue_description: string;
  recommendation: string;
  need_update: string;
}

interface ReportRuleItem {
  id: number;
  rule: { name: string; description: string };
  result_judgement: string | null;
  reason: string | null;
}

interface ReviewDetailModalProps {
  isModal?: boolean;
  visible: boolean;
  record: Record<string, any>;
  onClose: () => void;
}

const useContainerWidth = (ref: React.RefObject<HTMLElement | null>) => {
  const [width, setWidth] = useState(0);
  const updateWidth = () => {
    if (ref?.current) setWidth(ref.current.offsetWidth);
  };
  useEffect(() => {
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [ref]);
  return width;
};

const ReviewDetailModal: React.FC<ReviewDetailModalProps> = ({ isModal, visible, record, onClose }) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [fileUrl, setFileUrl] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [auditDetails, setAuditDetails] = useState<Record<string, any>>({});
  const [pageWidthPt, setPageWidthPt] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const containerWidth = useContainerWidth(containerRef);
  const [reportRules, setReportRules] = useState<ReportRuleItem[]>([]);
  const [searchParams] = useSearchParams();
  const ruleId = searchParams.get('rule');

  const resultOptions = [
    { label: '正确', value: '正确' },
    { label: '不正确', value: '不正确' },
    { label: '部分正确', value: '部分正确' },
    { label: '不确定', value: '不确定' },
  ];

  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);
      try {
        if (!record?.id) return;
        const response = await api.get(`/api/reviews/${record.id}/`);
        setAuditDetails(response.data);
        
        const reportId = record.report ?? record.id; // report ID
        const ruleParam = ruleId ? `&rule_id=${ruleId}` : '';
        const url = `/api/report-rules/?report_id=${reportId}${ruleParam}`;

        console.log('Fetching report rules from:', url);

        const rulesRes = await api.get<ReportRuleItem[] | null>(url);
        if (rulesRes.data) setReportRules(rulesRes.data);

        try {
          const file = await api.get(`api/report/download/${record.id}/`, { responseType: 'blob' });
          setFileUrl(file.data);
        } catch (fileErr) {
          console.error('Failed to fetch report file:', fileErr);
          setError('无法加载报告文件');
        }
      } catch (err) {
        setError('加载审核详情失败');
      } finally {
        setLoading(false);
      }
    };
    if (visible) fetchDetails();
  }, [visible, record?.id]);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setLoading(false);
  };

  const onPageLoadSuccess = (page: any) => {
    if (page && page._pageInfo && page._pageInfo.view) setPageWidthPt(page._pageInfo.view[2]);
  };

  const composeAuditResult = (result: string): string => {
    try {
      const issues: AuditIssue[] = JSON.parse(result);
      if (!Array.isArray(issues) || issues.length === 0) return '';
      return issues
        .map(issue => `审核内容：${issue.issue_description}\n审核结果：${issue.need_update === "true" ? "审核不通过" : "审核通过"}\n建议：${issue.recommendation}`)
        .join('\n\n');
    } catch {
      return String(result);
    }
  };

  const getSafeMarkdownContent = (content: string | null | undefined): string => {
    const result = composeAuditResult(content || '');
    if (typeof result !== 'string') return '';
    return result.trim();
  };

  const handleRuleChange = (index: number, field: 'result_judgement' | 'reason', value: string) => {
    const newRules = [...reportRules];
    newRules[index] = { ...newRules[index], [field]: value };
    setReportRules(newRules);
  };

  const saveRule = async (rule: ReportRuleItem) => {
    try {
      await api.patch(`/api/report-rules/${rule.id}/`, {
        result_judgement: rule.result_judgement,
        reason: rule.reason,
      });
      message.success('保存成功');
    } catch (err) {
      console.error(err);
      message.error('保存失败');
    }
  };

  const renderRules = () => (
    <div style={{ overflowY: 'auto', paddingRight: 8 }}>
      {reportRules.length === 0 ? (
        <p>暂无规则</p>
      ) : (
        reportRules.map((rule, index) => (
          <div key={rule.id} style={{ marginBottom: 16, padding: 12, border: '1px solid #f0f0f0', borderRadius: 4 }}>
            <Text strong>{rule.rule?.name}</Text>
            <p>{rule.rule?.description}</p>
            <Form layout="vertical">
              <Form.Item label="结果判定">
                <Select
                  value={rule.result_judgement || undefined}
                  onChange={value => handleRuleChange(index, 'result_judgement', value)}
                  placeholder="请选择"
                >
                  {resultOptions.map(opt => (
                    <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item label="理由标签">
                <Input
                  value={rule.reason || ''}
                  onChange={e => handleRuleChange(index, 'reason', e.target.value)}
                  placeholder="请输入理由"
                />
              </Form.Item>
              <Button type="primary" onClick={() => saveRule(rule)}>保存</Button>
            </Form>
          </div>
        ))
      )}
    </div>
  );

  const contentHTML = () => (
    <div style={{ display: 'flex', flexDirection: 'column', height: '80vh', gap: 16 }}>
      {/* 顶部：报告信息平铺 */}
      <div style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 16 }}>
        <Text strong>报告编号:</Text><Text>{auditDetails?.reportNumber}</Text>
        <Text strong>审核抬头:</Text><Text>{auditDetails?.titleCompany}</Text>
        <Text strong>审核地址:</Text><Text>{auditDetails?.address}</Text>
        <Text strong>报告日期:</Text><Text>{auditDetails?.reportDate}</Text>
        <Text strong>审核日期:</Text><Text>{auditDetails?.reviewDate}</Text>
        <Text strong>审核状态:</Text><Text>{auditDetails?.reviewStatus}</Text>
      </div>

      {/* 下方左右布局 */}
      <div style={{ display: 'flex', flex: 1, gap: 16, minHeight: 0 }}>
        {/* 左侧 PDF */}
        <div style={{ flex: 2, overflowY: 'auto' }} ref={containerRef}>
          {loading ? (
            <Spin tip="加载中..." />
          ) : error ? (
            <p style={{ color: 'red', textAlign: 'center', padding: '20px' }}>{error}</p>
          ) : fileUrl ? (
            <Document
              file={fileUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={(err) => setError(`PDF 加载失败: ${err.message}`)}
              loading={<Spin tip="正在加载 PDF..." />}
            >
              {Array.from(new Array(numPages || 0), (_, index) => (
                <div key={`page_${index + 1}`} style={{ marginBottom: 16 }}>
                  <Page
                    pageNumber={index + 1}
                    width={containerWidth}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    onLoadSuccess={onPageLoadSuccess}
                  />
                </div>
              ))}
            </Document>
          ) : (
            <p style={{ textAlign: 'center', padding: 20 }}>无法加载报告预览</p>
          )}
        </div>

        {/* 右侧：审核结果 + 规则 */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 16, maxHeight: '100%' }}>
          <div style={{ flex: 1, overflowY: 'auto', maxHeight: '50%' }}>
            <Title level={4}>审核结果</Title>
            {(auditDetails.audit_histories || []).length > 0 ? (
              auditDetails.audit_histories.map((result: any, index: number) => (
                <div key={index} style={{ marginBottom: 12 }}>
                  <ReactMarkdown>{String(getSafeMarkdownContent(result.audit_result))}</ReactMarkdown>
                </div>
              ))
            ) : (
              <p style={{ textAlign: 'center', padding: 20 }}>暂无审核结果</p>
            )}
          </div>

          <div style={{ flex: 1, overflowY: 'auto', maxHeight: '50%', borderTop: '1px solid #f0f0f0', paddingTop: 8 }}>
            <Title level={4}>报告规则</Title>
            {renderRules()}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {isModal ? (
        <Modal
          title="审核详情"
          visible={visible}
          onCancel={onClose}
          footer={null}
          width="90%"
          style={{ top: 20 }}
        >
          {contentHTML()}
        </Modal>
      ) : (
        contentHTML()
      )}
    </>
  );
};

export default ReviewDetailModal;
